import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/Badge';
import { RefreshCw, RotateCcw, AlertTriangle } from 'lucide-react';
import { api } from '@/services/apiClient';
import ProviderSettingsCard from './ProviderSettingsCard';
import ModelEditModal from './ModelEditModal';

interface ModelSetting {
  id: number;
  provider_name: string;
  model_id: string;
  display_name: string;
  enabled: boolean;
  is_default: boolean;
  config: Record<string, any>;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

interface ProviderSetting {
  id: number;
  provider_name: string;
  display_name: string;
  enabled: boolean;
  is_default: boolean;
  config: Record<string, any>;
  models: ModelSetting[];
  created_at: string;
  updated_at: string;
}

interface VramSummary {
  total_vram_gb: number;
  used_vram_gb: number;
  models: Array<{ model_id: string; estimated_gb: number; enabled: boolean }>;
  warning: string | null;
}

interface ModalState {
  open: boolean;
  mode: 'add' | 'edit';
  providerName: string;
  model?: ModelSetting;
}

const AIProviderSettings: React.FC = () => {
  const [providers, setProviders] = useState<ProviderSetting[]>([]);
  const [vram, setVram] = useState<VramSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalState, setModalState] = useState<ModalState>({
    open: false, mode: 'add', providerName: '',
  });
  const [checkingAll, setCheckingAll] = useState(false);
  const [healthCheckTrigger, setHealthCheckTrigger] = useState(0);
  const doneCount = useRef(0);

  const fetchData = useCallback(async () => {
    try {
      setError(null);
      const [provRes, vramRes] = await Promise.all([
        api.get('/admin/ai/providers/settings'),
        api.get('/admin/ai/providers/vram'),
      ]);
      setProviders(provRes.data);
      setVram(vramRes.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleProviderUpdate = async (name: string, update: Record<string, any>) => {
    try {
      const res = await api.put(`/admin/ai/providers/settings/${name}`, update);
      setProviders(prev => prev.map(p => p.provider_name === name ? res.data : p));
      // VRAM도 갱신
      const vramRes = await api.get('/admin/ai/providers/vram');
      setVram(vramRes.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  const handleModelAdd = (providerName: string) => {
    setModalState({ open: true, mode: 'add', providerName });
  };

  const handleModelEdit = (providerName: string, model: ModelSetting) => {
    setModalState({ open: true, mode: 'edit', providerName, model });
  };

  const handleModelDelete = async (providerName: string, modelId: string) => {
    if (!confirm(`모델 "${modelId}"를 삭제하시겠습니까?`)) return;
    try {
      const res = await api.delete(`/admin/ai/providers/settings/${providerName}/models/${modelId}`);
      setProviders(prev => prev.map(p => p.provider_name === providerName ? res.data : p));
      const vramRes = await api.get('/admin/ai/providers/vram');
      setVram(vramRes.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  const handleModalSave = async (data: Record<string, any>) => {
    try {
      let res;
      if (modalState.mode === 'add') {
        res = await api.post(`/admin/ai/providers/settings/${modalState.providerName}/models`, data);
      } else {
        res = await api.put(
          `/admin/ai/providers/settings/${modalState.providerName}/models/${modalState.model?.model_id}`,
          data,
        );
      }
      setProviders(prev => prev.map(p => p.provider_name === modalState.providerName ? res.data : p));
      setModalState({ open: false, mode: 'add', providerName: '' });
      const vramRes = await api.get('/admin/ai/providers/vram');
      setVram(vramRes.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  const handleCheckAll = () => {
    setCheckingAll(true);
    doneCount.current = 0;
    setHealthCheckTrigger(prev => prev + 1);
  };

  const handleCheckAllDone = useCallback(() => {
    doneCount.current += 1;
    if (doneCount.current >= providers.length) {
      setCheckingAll(false);
    }
  }, [providers.length]);

  const handleReset = async () => {
    if (!confirm('모든 AI Provider 설정을 YAML 기본값으로 초기화하시겠습니까?')) return;
    try {
      const res = await api.post('/admin/ai/providers/settings/reset');
      setProviders(res.data);
      const vramRes = await api.get('/admin/ai/providers/vram');
      setVram(vramRes.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">AI 제공자 설정</h1>
          <p className="text-sm text-gray-500 mt-1">
            AI Provider와 모델을 관리합니다. 변경 사항은 즉시 반영됩니다.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleCheckAll} disabled={checkingAll}>
            <RefreshCw className={`h-4 w-4 mr-1 ${checkingAll ? 'animate-spin' : ''}`} />
            {checkingAll ? '상태 확인 중...' : '전체 상태 확인'}
          </Button>
          <Button variant="outline" size="sm" onClick={handleReset}>
            <RotateCcw className="h-4 w-4 mr-1" />
            기본값 초기화
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* VRAM Warning for local provider */}
      {vram?.warning && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <strong>VRAM 경고:</strong> {vram.warning}
          </AlertDescription>
        </Alert>
      )}

      {/* VRAM Summary */}
      {vram && (
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-sm font-medium">로컬 모델 VRAM 사용량 (추정)</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      vram.used_vram_gb > vram.total_vram_gb ? 'bg-red-500' : 'bg-blue-500'
                    }`}
                    style={{ width: `${Math.min(100, (vram.used_vram_gb / vram.total_vram_gb) * 100)}%` }}
                  />
                </div>
              </div>
              <span className="text-sm font-mono whitespace-nowrap">
                {vram.used_vram_gb}GB / {vram.total_vram_gb}GB
              </span>
              {vram.used_vram_gb > vram.total_vram_gb && (
                <Badge variant="error">초과</Badge>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Provider Cards */}
      {providers.map(provider => (
        <ProviderSettingsCard
          key={provider.provider_name}
          provider={provider}
          vram={vram}
          onUpdate={handleProviderUpdate}
          onModelAdd={handleModelAdd}
          onModelEdit={handleModelEdit}
          onModelDelete={handleModelDelete}
          healthCheckTrigger={healthCheckTrigger}
          onHealthCheckDone={handleCheckAllDone}
        />
      ))}

      {/* Model Edit Modal */}
      <ModelEditModal
        open={modalState.open}
        mode={modalState.mode}
        providerName={modalState.providerName}
        model={modalState.model}
        onSave={handleModalSave}
        onClose={() => setModalState({ open: false, mode: 'add', providerName: '' })}
      />
    </div>
  );
};

export default AIProviderSettings;
